import { Form, Radio } from 'antd';
import { useContext, useEffect } from 'react';

import { SCard } from 'app/components/StaycationUI';
import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import type { OpeningConfigurationType } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import {
  PRICE_COST_TYPE,
  PRICE_DIFFERENTIATION_MODE,
} from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import { ExperienceType } from 'app/typings/experiences';

import { ConstantPriceConfiguration } from './_components/ConstantPriceConfiguration';
import { DailyPriceConfiguration } from './_components/DailyPriceConfiguration';

const RULES = [{ required: true, message: 'Required' }];

export const PriceConfiguration = () => {
  const { isViewMode } = useContext(ViewEditModeContext);

  const form = Form.useFormInstance();
  const hotelId = Form.useWatch(['hotelId'], form);
  const countryId = Form.useWatch(['countryId'], form);
  const experienceType = Form.useWatch(['experienceType'], form);
  const openingConfiguration = Form.useWatch(['openingConfiguration'], form);

  const isAlwaysIncludedWithNight = Form.useWatch(
    'isAlwaysIncludedWithNight',
    form
  );

  useEffect(() => {
    if (
      isAlwaysIncludedWithNight &&
      !!openingConfiguration &&
      openingConfiguration.priceConfiguration.priceDifferentiationMode ===
        PRICE_DIFFERENTIATION_MODE.NONE
    ) {
      const currentConfig =
        openingConfiguration.priceConfiguration.configuration;

      form.setFieldsValue({
        openingConfiguration: {
          priceConfiguration: {
            priceDifferentiationMode: PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME,
            configuration: {
              daytimePrice: currentConfig?.price,
              childDaytimePrice: currentConfig?.childPrice,
            },
          },
        },
      });
    }
  }, [form, isAlwaysIncludedWithNight, openingConfiguration]);

  const isOriginal =
    !hotelId && countryId && experienceType === ExperienceType.STAYCATION;

  const hasFees =
    openingConfiguration?.priceConfiguration?.cost === PRICE_COST_TYPE.PAID;
  const configurationType: OpeningConfigurationType =
    openingConfiguration?.priceConfiguration?.type;

  return (
    <SCard
      title="Price"
      subtitle="Configure the activity price depending on day, type and guests."
      className="experience-form price-configuration"
    >
      <FormLineLayout isRequired label="Cost" disabled={isViewMode}>
        <Form.Item
          name={['openingConfiguration', 'priceConfiguration', 'cost']}
        >
          <Radio.Group disabled={isViewMode}>
            <Radio value={PRICE_COST_TYPE.FREE} disabled={isOriginal}>
              Free of charge
            </Radio>
            <Radio value={PRICE_COST_TYPE.PAID}>Applicable fees</Radio>
          </Radio.Group>
        </Form.Item>
      </FormLineLayout>

      {hasFees && (
        <>
          {isViewMode && (
            <Form.Item
              name={[
                'openingConfiguration',
                'priceConfiguration',
                'priceDifferentiationMode',
              ]}
              noStyle
            />
          )}
          <FormLineLayout
            isRequired
            label="Configuration"
            disabled={isViewMode}
          >
            <Form.Item
              name={['openingConfiguration', 'priceConfiguration', 'type']}
              rules={RULES}
              initialValue={OPENING_CONFIGURATION_TYPE.CONSTANT}
            >
              <Radio.Group disabled={isViewMode}>
                <Radio value={OPENING_CONFIGURATION_TYPE.CONSTANT}>
                  Same every day
                </Radio>
                <Radio
                  value={OPENING_CONFIGURATION_TYPE.DAILY}
                  disabled={isOriginal}
                >
                  Depends on the day
                </Radio>
              </Radio.Group>
            </Form.Item>
          </FormLineLayout>
          {configurationType === OPENING_CONFIGURATION_TYPE.CONSTANT && (
            <ConstantPriceConfiguration isOriginal={isOriginal} />
          )}
          {configurationType === OPENING_CONFIGURATION_TYPE.DAILY && (
            <DailyPriceConfiguration />
          )}
        </>
      )}
    </SCard>
  );
};
